import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { Store, ShieldCheck, BadgeIndianRupee, LayoutDashboard, UserCheck, Eye, EyeOff } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const PORTAL_META = {
  customer: { title: "Welcome back", sub: "Sign in to shop, track orders and more." },
  vendor: { title: "Vendor sign in", sub: "Access your seller dashboard — catalogue, orders, settlements." },
  admin: { title: "Admin sign in", sub: "Marketplace control centre — restricted access." },
};

export default function AuthPage({ mode, portal = "customer" }) {
  const isLogin = mode === "login";
  const meta = PORTAL_META[portal] || PORTAL_META.customer;
  const { login, register, googleLogin, logout, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [googleCfg, setGoogleCfg] = useState(null);

  useEffect(() => {
    if (!user) return;
    if (portal === "vendor") {
      nav(user.role === "seller" || user.role === "admin" ? "/seller" : "/sell");
    } else if (portal === "admin") {
      if (user.role === "admin") nav("/admin");
    } else {
      nav(loc.state?.from || "/");
    }
  }, [user, nav, loc.state, portal]);

  useEffect(() => {
    api.get("/auth/google/config").then(({ data }) => setGoogleCfg(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const loggedUser = isLogin
        ? await login(form.email, form.password)
        : await register(form.name, form.email, form.password);
      if (portal === "admin" && loggedUser.role !== "admin") {
        logout();
        toast.error("This account does not have admin access");
        return;
      }
      if (portal === "vendor" && loggedUser.role === "customer") {
        toast.info("No seller account yet — register your business to start selling");
      } else {
        toast.success(isLogin ? "Welcome back" : "Account created");
      }
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const isAdmin = portal === "admin";
  const inputCls = isAdmin
    ? "w-full h-12 rounded-md border border-slate-700 bg-slate-800 px-4 text-sm text-white placeholder:text-slate-500 outline-none focus:border-blue-400"
    : "w-full h-12 rounded-md border border-slate-200 px-4 text-sm outline-none focus:border-blue-500";
  const btnCls = isAdmin
    ? "w-full rounded-full h-12 bg-blue-500 hover:bg-blue-400 font-semibold"
    : "w-full rounded-full h-12 bg-slate-900 hover:bg-slate-700 font-semibold";

  const formBody = (
    <>
      <h1 className={`font-heading text-3xl font-bold tracking-tight ${isAdmin ? "text-white" : ""}`}>{isLogin ? meta.title : "Create your account"}</h1>
      <p className={`text-sm mt-2 ${isAdmin ? "text-slate-400" : "text-slate-500"}`}>{isLogin ? meta.sub : "Join Hello Nanba to shop from thousands of sellers."}</p>

      <form onSubmit={submit} className="mt-8 space-y-4">
        {!isLogin && (
          <input data-testid="register-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Full name" required className={inputCls} />
        )}
        <input data-testid="auth-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="Email" required className={inputCls} />
        <div className="relative">
          <input data-testid="auth-password" type={showPw ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Password" required minLength={6} className={`${inputCls} pr-12`} />
          <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPw(!showPw)}
            aria-label="Toggle password visibility"
            className={`absolute right-4 top-1/2 -translate-y-1/2 transition-colors ${isAdmin ? "text-slate-500 hover:text-white" : "text-slate-400 hover:text-slate-900"}`}>
            {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <Button data-testid="auth-submit-btn" type="submit" disabled={busy} className={btnCls}>
          {busy ? "Please wait…" : isLogin ? "Sign in" : "Create account"}
        </Button>
      </form>

      {googleCfg?.enabled && portal !== "admin" && (
        <div className="mt-6 flex justify-center" data-testid="google-login-wrap">
          <GoogleOAuthProvider clientId={googleCfg.client_id}>
            <GoogleLogin
              onSuccess={async (res) => {
                try {
                  await googleLogin(res.credential);
                  toast.success("Signed in with Google");
                  nav(loc.state?.from || "/");
                } catch (err) {
                  toast.error(formatError(err));
                }
              }}
              onError={() => toast.error("Google sign-in failed")}
            />
          </GoogleOAuthProvider>
        </div>
      )}

      <p className={`text-sm mt-8 text-center ${isAdmin ? "text-slate-400" : "text-slate-500"}`}>
        {isLogin ? (
          portal === "vendor" ? (
            <>New vendor? <Link to="/sell" data-testid="goto-seller-register" className="text-blue-600 font-semibold">Register your business</Link></>
          ) : portal === "customer" ? (
            <>New here? <Link to="/register" data-testid="goto-register" className="text-blue-600 font-semibold">Create an account</Link></>
          ) : null
        ) : (
          <>Already have an account? <Link to="/login" data-testid="goto-login" className="text-blue-600 font-semibold">Sign in</Link></>
        )}
      </p>
      {portal === "customer" && isLogin && (
        <p className="text-xs text-slate-400 mt-4 text-center">
          Selling products? <Link to="/sell" className="text-blue-600 font-semibold">Register as a vendor</Link>
        </p>
      )}
    </>
  );

  if (isAdmin) {
    return (
      <div data-testid="admin-login-page" className="px-4 sm:px-8 mt-16 flex justify-center">
        <div className="w-full max-w-md rounded-2xl bg-slate-900 p-10 shadow-2xl">
          <ShieldCheck className="w-8 h-8 text-blue-400 mb-6" />
          {formBody}
        </div>
      </div>
    );
  }

  if (portal === "vendor" && isLogin) {
    return (
      <div data-testid="vendor-login-page" className="max-w-5xl mx-auto px-4 sm:px-8 mt-16">
        <div className="grid md:grid-cols-2 rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white">
          <div className="bg-slate-900 text-white p-10 sm:p-12 flex flex-col justify-between gap-10">
            <div>
              <Store className="w-8 h-8 text-blue-400" />
              <h2 className="font-heading text-3xl font-bold tracking-tight mt-6">Hello Nanba for Vendors</h2>
              <ul className="mt-8 space-y-5 text-sm text-slate-300">
                <li className="flex gap-3 items-start"><BadgeIndianRupee className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />Zero commission — you keep 100% of every sale</li>
                <li className="flex gap-3 items-start"><UserCheck className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />One-time platform fee, personally admitted by our team</li>
                <li className="flex gap-3 items-start"><LayoutDashboard className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />Your own dashboard: catalogue, inventory, orders, returns, settlements</li>
              </ul>
            </div>
            <p className="text-xs text-slate-400">New vendor? <Link to="/sell" className="text-blue-400 font-semibold">Register your business</Link> — applications are reviewed by marketplace ops.</p>
          </div>
          <div className="p-10 sm:p-12">{formBody}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-8 mt-16" data-testid={isLogin ? "customer-login-page" : "register-page"}>
      {formBody}
    </div>
  );
}
